import {
  ActionButton,
  ActionFooter,
  Banner,
  Caption,
  EnumBanner,
  Eyebrow,
  IconTile,
  StrongValue,
  SubsectionTitle,
} from '@if/mui'
import { Card, CardContent, Stack } from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  FileSignature,
  FileText,
  ShieldCheck,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { ECurrentCustomerStep } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ECreditApplicationStatusMessage, getStepStatus } from '../viewCreditApp-selectors'

type StageState = 'completed' | 'current' | 'pending'

// Header icon per step (mirrors the mockup).
const STEP_ICON = {
  [ECurrentCustomerStep.Draft]: ShieldCheck,
  [ECurrentCustomerStep.Prequal]: ShieldCheck,
  [ECurrentCustomerStep.Credit]: CreditCard,
  [ECurrentCustomerStep.IncomeVerification]: FileText,
  [ECurrentCustomerStep.Contract]: FileSignature,
  [ECurrentCustomerStep.Funding]: Wallet,
} satisfies Record<ECurrentCustomerStep, LucideIcon>

// State → the "CURRENT/COMPLETED/PENDING STAGE" eyebrow label key.
const STAGE_LABEL_KEY = {
  completed: 'viewCreditApplication.stage.completed',
  current: 'viewCreditApplication.stage.current',
  pending: 'viewCreditApplication.stage.pending',
} as const satisfies Record<StageState, string>

// Pending steps only — the "why it's locked" reason, keyed by the stage that has to finish first.
const PENDING_REASON_KEY = {
  [ECurrentCustomerStep.Prequal]: 'viewCreditApplication.stageBanner.pending.afterPrequal',
  [ECurrentCustomerStep.Credit]: 'viewCreditApplication.stageBanner.pending.afterCredit',
  [ECurrentCustomerStep.IncomeVerification]: 'viewCreditApplication.stageBanner.pending.afterIncomeVerification',
  [ECurrentCustomerStep.Contract]: 'viewCreditApplication.stageBanner.pending.afterContract',
} as const

interface Props {
  step: ECurrentCustomerStep
  active: ECurrentCustomerStep
  creditApp: CreditApplication
  // The current-step banner's description (per-step text). Ignored for completed/pending.
  bannerDescription?: ReactNode
  // Suppresses the stage-level status banner (current + completed) when the content shows its own per-applicant banners.
  hideStatusBanner?: boolean
  // The reskinned step content shown under the banner (not rendered for pending steps).
  children?: ReactNode
  // The steps shown as tabs, in order — the navigation band's neighbours come from this list.
  steps: ECurrentCustomerStep[]
  onNavigate: (step: ECurrentCustomerStep) => void
}

// Both navigation buttons keep one footprint whatever the stage name, so the band doesn't shift between stages.
const BUTTON_SX = { width: 260, minHeight: 56 }

// Places the shown step relative to the current one: done / current / not-yet-reached.
const stageStateOf = (
  step: ECurrentCustomerStep,
  active: ECurrentCustomerStep,
  steps: ECurrentCustomerStep[],
): StageState => {
  const stepIndex = steps.indexOf(step)
  const activeIndex = steps.indexOf(active)
  if (stepIndex < activeIndex) return 'completed'
  if (stepIndex === activeIndex) return 'current'
  return 'pending'
}

// Left-column card: stage header + status banner (3 states) + the step's content.
const StagePanel = ({
  step,
  active,
  creditApp,
  bannerDescription,
  hideStatusBanner,
  children,
  steps,
  onNavigate,
}: Props) => {
  const { t } = useTranslation()
  const state = stageStateOf(step, active, steps)

  const index = steps.indexOf(step)
  const previousStep = index > 0 ? steps[index - 1] : null
  const nextStep = index >= 0 && index < steps.length - 1 ? steps[index + 1] : null

  const pendingReasonKey = previousStep
    ? PENDING_REASON_KEY[previousStep as keyof typeof PENDING_REASON_KEY]
    : undefined

  const navigationLabel = (direction: string, target: ECurrentCustomerStep) => (
    <Stack component="span" sx={{ alignItems: 'flex-start' }}>
      <Caption>{direction}</Caption>
      <StrongValue>{t(`creditStatus.${target}`)}</StrongValue>
    </Stack>
  )

  // The status callout: green when done, the real status when current, neutral when locked.
  const banner = useMemo(() => {
    if (state === 'pending') {
      return (
        <Banner severity="info" icon={Clock} title={t('viewCreditApplication.stageBanner.pendingTitle')}>
          {pendingReasonKey && t(pendingReasonKey)}
        </Banner>
      )
    }
    // Steps that render their own per-applicant banners suppress the stage-level status callout.
    if (hideStatusBanner) return null
    if (state === 'completed') {
      return (
        <EnumBanner set={ECreditApplicationStatusMessage} value={ECreditApplicationStatusMessage.Completed}>
          {t('viewCreditApplication.stageBanner.completedMessage')}
        </EnumBanner>
      )
    }
    const currentStatus = getStepStatus(step, creditApp)
    return currentStatus ? (
      <EnumBanner set={ECreditApplicationStatusMessage} value={currentStatus}>
        {bannerDescription}
      </EnumBanner>
    ) : null
  }, [state, hideStatusBanner, pendingReasonKey, step, creditApp, bannerDescription, t])

  return (
    <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconTile icon={STEP_ICON[step]} size="sm" color={state === 'completed' ? 'success' : 'primary'} />
            <Stack spacing={0.25}>
              <Eyebrow>{t(STAGE_LABEL_KEY[state])}</Eyebrow>
              <SubsectionTitle>{t(`creditStatus.${step}`)}</SubsectionTitle>
            </Stack>
          </Stack>
          {banner}
          {state !== 'pending' && children}
          {state === 'pending' && <Caption>{t('viewCreditApplication.stageBanner.pendingFooter')}</Caption>}
        </Stack>
      </CardContent>
      <ActionFooter
        leading={
          previousStep && (
            // The mockup's Previous carries no outline — the one look the tone/emphasis pair can't express.
            <ActionButton
              startIcon={ChevronLeft}
              onClick={() => onNavigate(previousStep)}
              sx={{ ...BUTTON_SX, justifyContent: 'flex-start', border: 'none', backgroundColor: 'transparent' }}
            >
              {navigationLabel(t('common.previous'), previousStep)}
            </ActionButton>
          )
        }
      >
        {nextStep && (
          <ActionButton
            endIcon={ChevronRight}
            onClick={() => onNavigate(nextStep)}
            sx={{ ...BUTTON_SX, justifyContent: 'space-between' }}
          >
            {navigationLabel(t('common.next'), nextStep)}
          </ActionButton>
        )}
      </ActionFooter>
    </Card>
  )
}

export default StagePanel
